import md5 from 'blueimp-md5'

import type { V2DatasetItemPayload } from '@/store/types/V2DatasetItemPayload'
import { parseRGBA, getRGBAColorHash, rgbaString } from '@/uiKit/colorPalette'
import type { RGBA } from '@/uiKit/colorPalette'
import type { WorkflowStage } from '@/modules/Workflows/types'
import { StageType } from '@/store/types/StageType'
import { StageActionType } from '@/store/types/StageActionType'
import type { V2WorkflowStageInstancePayload } from '@/store/types/V2WorkflowStageInstancePayload'
import type { V2WorkflowStagePayload } from '@/store/types/V2WorkflowStagePayload'
import type { V2WorkflowItemStatePayload } from '@/store/types/V2WorkflowItemStatePayload'

type Colorable = {
  metadata: {
    _color: string
  }
  name: string
}

export const classColor = <T extends Colorable>(annotationClass: T): RGBA =>
  annotationClass.metadata._color
    ? parseRGBA(annotationClass.metadata._color)
    : getRGBAColorHash(annotationClass.name)

export const classColorRGBAString = <T extends Colorable>(annotationClass: T): string =>
  rgbaString(classColor(annotationClass))

export const chromaHash = (value: string, bars: number = 3): string[] => {
  const hash = md5(value)
  const colors = hash.match(/([\dABCDEF]{6})/gi)
  return colors?.slice(0, bars) || []
}

export const resolveSlotThumbnailV2 = (
  item: V2DatasetItemPayload,
  slotName: string,
): string | null => {
  if (!item.slots?.length) {
    return null
  }

  const slot = item.slots.find((slot) => slot.slot_name === slotName)

  if (!slot) {
    return null
  }

  return slot.thumbnail_url || null
}

/**
 * A list of edge names for currently "supported" stages which represent following
 * the happy path of a workflow.
 */
const HAPPY_PATH_EDGES = [
  'accepted',
  'approve',
  'default',
  'parallel',
  'pass',
  'passed',
  'rule-0',
  'succeeded',
]

export const getFollowingStage = (
  stages?: WorkflowStage[] | null,
  stageId?: string | null,
): WorkflowStage | null => {
  if (!stages || !stageId) {
    return null
  }

  const currentStage = stages.find((s) => s.id === stageId)
  const firstHappyEdge = currentStage?.edges.find((e) => HAPPY_PATH_EDGES.includes(e.name))
  return stages.find((s) => s.id === firstHappyEdge?.target_stage_id) ?? null
}

export const getNextStage = (
  instance: V2WorkflowStageInstancePayload,
  itemState: V2WorkflowItemStatePayload,
): V2WorkflowStagePayload | undefined =>
  itemState.workflow.stages.find((s) =>
    instance.stage.edges.some(
      // next stage is the implicit default next stage.
      // that means only certain edges are considered
      // - approve for review stage
      // - default for annotate stage (and all other stages)
      (e) => ['default', 'approve'].includes(e.name) && e.target_stage_id === s.id,
    ),
  )

const standardStageMapping: Record<
  Exclude<StageType, typeof StageType.Dataset>,
  StageActionType
> = {
  [StageType.Archive]: StageActionType.SendToArchive,
  [StageType.Annotate]: StageActionType.SendToAnnotate,
  [StageType.Complete]: StageActionType.MarkAsComplete,
  [StageType.ConsensusEntrypoint]: StageActionType.SendToConsensus,
  [StageType.ConsensusTest]: StageActionType.SendToTest,
  [StageType.Logic]: StageActionType.SendToLogic,
  [StageType.Model]: StageActionType.SendToModel,
  [StageType.Review]: StageActionType.SendToReview,
  [StageType.Discard]: StageActionType.Archive,
  [StageType.Webhook]: StageActionType.SendToWebhook,
  [StageType.Sampling]: StageActionType.SendToSampling,
  [StageType.ParallelBlind]: StageActionType.SendToParallelBlind,
  [StageType.AiAssistedReview]: StageActionType.SendToAiAssistedReview,
}

const sentToNextReviewStageMapping: Record<
  Exclude<StageType, typeof StageType.Dataset>,
  StageActionType
> = { ...standardStageMapping, [StageType.Review]: StageActionType.SendToNextReview }
const MAPPING: {
  [k in StageType]: Record<Exclude<StageType, typeof StageType.Dataset>, StageActionType>
} = {
  [StageType.Archive]: { ...standardStageMapping },
  [StageType.Annotate]: { ...standardStageMapping },
  [StageType.Review]: { ...sentToNextReviewStageMapping },
  [StageType.Dataset]: { ...sentToNextReviewStageMapping },
  [StageType.Model]: { ...sentToNextReviewStageMapping },
  [StageType.Complete]: {
    ...standardStageMapping,
    [StageType.Annotate]: StageActionType.MarkAsComplete,
    [StageType.Complete]: StageActionType.MarkAsComplete,
    [StageType.Model]: StageActionType.MarkAsComplete,
    [StageType.Review]: StageActionType.MarkAsComplete,
  },
  [StageType.ConsensusEntrypoint]: { ...standardStageMapping },
  [StageType.ConsensusTest]: { ...sentToNextReviewStageMapping },
  [StageType.Logic]: { ...standardStageMapping },
  [StageType.Discard]: { ...standardStageMapping },
  [StageType.Webhook]: { ...standardStageMapping },
  [StageType.Sampling]: { ...standardStageMapping },
  [StageType.ParallelBlind]: { ...standardStageMapping },
  [StageType.AiAssistedReview]: { ...standardStageMapping },
}
/**
 * Return basic mapping when transitioning between two stages of specified types,
 * as for "from" => "to" => "action"
 *
 * This menas that transitioning from "from" type to "to" type implies the
 * returned action is happening.
 *
 * This is mainly used to decide which text to show on the continue button label.
 */
export const mapStageActiontype = (
  from: StageType,
  to: Exclude<StageType, typeof StageType.Dataset>,
): StageActionType => MAPPING[from][to]
